import {
  PipelineState,
  useWalkthroughBackupUpload,
  isValidationSuccess,
  WalkthroughBackupFileContents,
} from 'src/pages/Settings/components/WalkthroughBackupUploader/Hooks/useWalkthroughBackupUpload';
import { useDropzone } from 'react-dropzone';
import { StepIndicator } from 'src/pages/Settings/components/WalkthroughBackupUploader/Components/StepIndicator/StepIndicator';
import { JSONDropZone } from 'src/pages/Settings/components/WalkthroughBackupUploader/Components/JSONDropZone/JSONDropZone';
import { LoadingStep } from 'src/pages/Settings/components/WalkthroughBackupUploader/Components/LoadingStep/LoadingStep';
import { ErrorCard } from 'src/pages/Settings/components/WalkthroughBackupUploader/Components/ErrorCard/ErrorCard';
import { SuccessCard } from 'src/pages/Settings/components/WalkthroughBackupUploader/Components/SuccessCard/SuccessCard';
import {
  FilePreviewCard
} from 'src/pages/Settings/components/WalkthroughBackupUploader/Components/FilePrewviewCard/FilePreviewCard';
import './ WalkthroughBackupUploader.scss';

const LOADING_STATES: PipelineState[] = [
  'validating',
  'checking-status',
  'fetching-url',
  'uploading',
];

const ERROR_STATES: PipelineState[] = [
  'error-invalid-json',
  'error-missing-fields',
  'error-not-eligible',
  'error-network',
  'error-upload-failed',
];

const WalkthroughBackupUploader = () => {
  const {
    state,
    file,
    validationResult,
    enrichment,
    missingFields,
    errorDetail,
    onFileDrop,
    onConfirm,
    reset,
  } = useWalkthroughBackupUpload();

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    accept: '.json',
    multiple: false,
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles[0]) onFileDrop(acceptedFiles[0]);
    },
  });

  const extractedContents: WalkthroughBackupFileContents | null =
    validationResult && isValidationSuccess(validationResult)
      ? validationResult.data
      : null;

  return (
    <div className="wbu_container">
      <div className="wbu_header">
        <span className="wbu_title">Walkthrough JSON Backup Uploader</span>
        <span className="wbu_badge">INTERNAL</span>
      </div>
      <p className="wbu_description">
        Restore a walkthrough from a JSON backup file. The file will be validated before any changes are made.
      </p>
      <StepIndicator currentState={state} />
      {state === 'idle' && (
        <JSONDropZone
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          open={open}
        />
      )}
      {state === 'file-preview' && extractedContents && enrichment && file && (
        <FilePreviewCard
          file={file}
          contents={extractedContents}
          enrichment={enrichment}
          onConfirm={onConfirm}
          onCancel={reset}
        />
      )}
      {LOADING_STATES.includes(state) && (
        <LoadingStep state={state} fileName={file?.name ?? ''} />
      )}
      {ERROR_STATES.includes(state) && (
        <ErrorCard
          state={state}
          missingFields={missingFields}
          errorDetail={errorDetail}
          fileContents={extractedContents}
          onReset={reset}
        />
      )}
      {state === 'success' && (
        <SuccessCard fileName={file?.name ?? ''} onUploadAnother={reset} />
      )}
      <p className="wbu_hint">
        Only <code>.json</code> backup files are accepted.
      </p>
    </div>
  );
};

export default WalkthroughBackupUploader;